import React, { useEffect, useState } from "react";
import { Card, Form, Input, Button, Table, Modal } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, ApartmentOutlined } from "@ant-design/icons";
import { createBusinessType, getBusinessTypes } from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";

const BusinessTypes = () => {
  const ADD_BUSINESS_TYPE = "Add Business Type";
  const [modalTitle, setModalTitle] = useState(ADD_BUSINESS_TYPE);
  const [modalVisible, setModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [businessTypesTableData, setBusinessTypesTableData] = useState([]);
  const [businessTypeForm] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    setIsDataLoading(true);
    getBusinessTypes()
      .then((response) => {
        setBusinessTypesTableData(response.data.data);
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData]);

  const showAddModal = () => {
    // Set Modal Title
    setModalTitle(t("business_type_add"));

    // Reset Form Fields
    businessTypeForm.resetFields();

    // Show Modal
    setModalVisible(true);
  };

  const handleAPIResponse = (data, type) => {
    if (data.success === true) {
      setReloadTableData(!reloadTableData);
      // Success
      if (type === ADD_BUSINESS_TYPE) {
        NotificationWithIcon("success", t("business_type_added"), data.message);
      } else {
        NotificationWithIcon(
          "success",
          t("business_type_updated"),
          t("business_type_updated_successfully")
        );
      }
    } else {
      // Error
      NotificationWithIcon("error", t("something_went_wrong"), data.message);
    }
    // Reset Form Fields
    businessTypeForm.resetFields();
    // Hide Modal
    setModalVisible(false);
  };

  const handleAddEditBusinessType = () => {
    businessTypeForm
      .validateFields()

      .then((values) => {
        // Set Loading True
        setLoading(true);

        // Sending Request to API

        createBusinessType(values)
          .then((response) => {
            let data = response.data || response.response.data;
            if (data.success === true) {
              return handleAPIResponse(data, ADD_BUSINESS_TYPE);
            } else {
              let errors = data?.data?.errors;
              if (errors && Object.keys(errors).length) {
                // Error
                NotificationWithIcon(
                  "error",
                  t("something_went_wrong"),
                  errors[Object.keys(errors)[0]]
                );
              }
            }
          })
          .finally(() => {
            // Set Loading False
            setLoading(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleCancel = () => {
    // Reset Form Fields
    businessTypeForm.resetFields();
    setModalVisible(false);
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("menu_home"),
      icon: <HomeOutlined />,
    },
    {
      path: "business_types",
      breadcrumbName: t("menu_business_types"),
      icon: <ApartmentOutlined />,
    },
  ];

  const businessTypesTableColumns = [
    {
      title: "#",
      key: "index",
      width: "100px",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("name_en"),
      dataIndex: "name",
    },
    {
      title: t("name_ar"),
      dataIndex: "name_ar",
    },
  ];

  return (
    <>
      <Card style={{ marginBottom: "20px" }}>
        <CustomBreadcrumb items={breadcrumbRoutes} />
        <PageHeader
          style={{ padding: "0" }}
          title={t("menu_business_types")}
          ghost={false}
          extra={[
            <Button
              key="open-add-business-type-modal"
              type="primary"
              onClick={showAddModal}
            >
              + {t("business_type_add")}
            </Button>,
          ]}
        />
      </Card>
      <Card>
        <Table
          columns={businessTypesTableColumns}
          dataSource={businessTypesTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>
      <Modal
        open={modalVisible}
        title={modalTitle}
        onOk={handleAddEditBusinessType}
        onCancel={handleCancel}
        footer={[
          <Button key="back" loading={loading} onClick={handleCancel}>
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleAddEditBusinessType}
          >
            {modalTitle === ADD_BUSINESS_TYPE ||
            modalTitle === t("business_type_add")
              ? t("business_type_add")
              : t("update")}
          </Button>,
        ]}
      >
        <Form
          name="add-business-type-form"
          layout="vertical"
          onFinish={handleAddEditBusinessType}
          style={{ width: "100%", margin: "0" }}
          form={businessTypeForm}
        >
          <Form.Item
            name="name"
            label={t("name_en")}
            rules={[
              {
                required: true,
                message: t("validate_name_required"),
              },
              {
                min: 3,
                message: t("validate_name_min_characters"),
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name_ar"
            label={t("name_ar")}
            rules={[
              {
                required: true,
                message: t("validate_name_required"),
              },
              {
                min: 3,
                message: t("validate_name_min_characters"),
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>
          <Form.Item name="id" style={{ display: "none" }}>
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BusinessTypes;
